import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'
import { invaliderDepenses, invaliderFiches } from '../../lib/cache'
import { copier, decouperEnLiens, premierLien } from '../../lib/liens'
import { titreGenerique } from '../../lib/titres'
import { Blocs } from './Blocs'
import {
  api,
  ApiError,
  type Category,
  type Expense,
  type Item,
  type Me,
  type SpaceDetail,
} from '../../lib/api'
import { Cadenas, FicheProtegee, ReglagesDuVerrou, useVerrou } from './Verrou'
import { CategoryMark } from '../spaces/CategoryMark'
import { layout } from '../../design/tokens'

/**
 * Le texte d'une note, ses adresses rendues cliquables.
 *
 * Une recette collée dans le texte restait du texte : le lien ne s'ouvrait pas (correction S70).
 * `pre-wrap` est conservé — les retours à la ligne saisis font partie de ce qu'on a écrit.
 */
function TexteAvecLiens({ texte }: { texte: string }) {
  return (
    <p className="mt-4 text-body" style={{ maxWidth: '60ch', whiteSpace: 'pre-wrap' }}>
      {decouperEnLiens(texte).map((fragment, rang) =>
        fragment.lien ? (
          <a
            // Le rang suffit : les fragments ne sont ni réordonnés ni retirés, ils sont recalculés
            // en entier à chaque rendu du texte.
            key={rang}
            href={fragment.lien}
            target="_blank"
            rel="noreferrer noopener"
            className="font-semibold text-accent underline"
            style={{ wordBreak: 'break-word' }}
          >
            {fragment.texte}
          </a>
        ) : (
          <span key={rang}>{fragment.texte}</span>
        ),
      )}
    </p>
  )
}

/** Centimes entiers vers euros lisibles. Aucun flottant ne traverse ce calcul. */
function euros(centimes: number) {
  return `${Math.floor(centimes / 100)},${String(centimes % 100).padStart(2, '0')} €`
}

/**
 * Planche 07 du cahier des charges — la fiche.
 *
 * « Une seule action primaire : ouvrir. » Des deux actions de transformation de la planche 07,
 * seule « À l'agenda » subsiste. « Aux courses » a été retirée le 2026-08-19 : elle ouvrait un
 * champ où l'article se tapait à la main, soit exactement le geste qu'on fait dans la liste de
 * courses, précédé d'un détour par la fiche. Elle ne reviendra qu'en sachant lire les
 * ingrédients d'une recette (chantier ouvert, décision L15). Le menu secondaire porte le
 * changement de catégorie, l'archivage et la suppression définitive.
 */
export function ItemPage() {
  const { spaceId = '', itemId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [menu, setMenu] = useState(false)
  // Même repli que sur les cartes : une vignette refusée par le site laisse la place à l'aplat
  // de catégorie, plutôt qu'à un rectangle vide de 4/3 en tête de fiche.
  const [imageCassee, setImageCassee] = useState(false)
  const [agenda, setAgenda] = useState(false)
  const [quand, setQuand] = useState(() => new Date().toISOString().slice(0, 10))
  const [jusqua, setJusqua] = useState('')
  const [agendaMessage, setAgendaMessage] = useState<string | null>(null)

  // Ce que le serveur rend sans code : une fiche sous verrou arrive ici vidée de son contenu.
  const { data: ficheMasquee, isPending } = useQuery<Item>({
    queryKey: ['fiche', spaceId, itemId],
    queryFn: () => api.item(spaceId, itemId),
  })

  const verrou = useVerrou(spaceId, itemId)

  // La version ouverte prime quand elle existe. Elle ne vit que dans l'état du composant : en
  // sortant de la fiche elle disparaît, et y revenir redemande le code.
  const item = verrou.ouverte ?? ficheMasquee

  const { data: categories } = useQuery<readonly Category[]>({
    queryKey: ['espace', spaceId, 'categories'],
    queryFn: () => api.space(spaceId).then((detail) => detail.categories),
  })

  // Après une modification, la version ouverte se relit avec le code retenu : le cache des
  // requêtes ne contient que la fiche masquée, et s'en contenter afficherait un bloc
  // fraîchement enregistré comme absent.
  const rafraichir = async () => {
    await invaliderFiches(queryClient, spaceId)
    await verrou.relire()
  }

  // Correction du contenu. Le texte n'était modifiable nulle part : une note mal saisie ne
  // pouvait que se supprimer et se refaire.
  const [edition, setEdition] = useState(false)
  const [titre, setTitre] = useState('')
  const [texte, setTexte] = useState('')
  const [erreurEdition, setErreurEdition] = useState<string | null>(null)

  const ouvrirEdition = () => {
    setTitre(item?.title ?? '')
    setTexte(item?.description ?? '')
    setErreurEdition(null)
    setEdition(true)
  }

  const enregistrer = useMutation({
    // Le texte vide part en chaîne vide et non en `undefined` : c'est ainsi qu'on efface.
    mutationFn: () =>
      api.patchItem(spaceId, itemId, { title: titre.trim(), description: texte.trim() }),
    onSuccess: async () => {
      setEdition(false)
      await rafraichir()
    },
    onError: () => setErreurEdition("L'enregistrement a échoué."),
  })

  const classer = useMutation({
    mutationFn: (categoryId: string) => api.patchItem(spaceId, itemId, { categoryId }),
    onSuccess: async () => {
      setMenu(false)
      await rafraichir()
    },
  })

  const favori = useMutation({
    mutationFn: (valeur: boolean) => api.patchItem(spaceId, itemId, { isFavorite: valeur }),
    // L'étoile change à l'instant du geste, pas au retour du serveur : c'est un interrupteur,
    // et un interrupteur qui réagit une demi-seconde plus tard donne l'impression d'avoir raté.
    onMutate: async (valeur: boolean) => {
      await queryClient.cancelQueries({ queryKey: ['fiche', spaceId, itemId] })

      const precedent = queryClient.getQueryData<Item>(['fiche', spaceId, itemId])

      if (precedent) {
        queryClient.setQueryData<Item>(['fiche', spaceId, itemId], {
          ...precedent,
          isFavorite: valeur,
        })
      }

      return { precedent }
    },
    onError: (_erreur, _valeur, contexte) => {
      if (contexte?.precedent) {
        queryClient.setQueryData(['fiche', spaceId, itemId], contexte.precedent)
      }
    },
    onSettled: rafraichir,
  })

  // Planche 07 : « deux actions de transformation qui font vivre les modules ». Seule celle de
  // l'agenda est retenue — voir l'en-tête du fichier pour le sort de « Aux courses ».
  const envoyerAgenda = useMutation({
    mutationFn: () =>
      api.createEvent(spaceId, {
        title: item?.title ?? 'Sans titre',
        startsAt: new Date(`${quand}T00:00`).toISOString(),
        ...(jusqua ? { endsAt: new Date(`${jusqua}T23:59`).toISOString() } : {}),
        isAllDay: true,
        itemId: itemId,
      }),
    onSuccess: async () => {
      setAgendaMessage('Ajouté à l\'agenda.')
      setJusqua('')
      await queryClient.invalidateQueries({ queryKey: ['agenda', spaceId] })
      await queryClient.invalidateQueries({ queryKey: ['aujourdhui'] })
    },
    onError: () => setAgendaMessage("L'ajout a échoué."),
  })

  const [erreurImage, setErreurImage] = useState<string | null>(null)

  const envoyerImage = useMutation({
    // Une image collée devient un bloc image, comme celle qu'on ajoute par le bouton. Elle
    // atterrissait dans une bande séparée, et la fiche offrait alors deux endroits pour la même
    // chose — un bouton « + Image » et un encart « Images » (correction S83).
    mutationFn: async (fichier: File) => {
      const image = await api.uploadImage(spaceId, itemId, fichier)
      await api.addBlock(spaceId, itemId, { kind: 'image', attachmentId: image.id })
    },
    onSuccess: async () => {
      setErreurImage(null)
      await rafraichir()
    },
    onError: (cause: unknown) =>
      setErreurImage(cause instanceof ApiError ? cause.message : "L'envoi a échoué."),
  })

  const retirerImage = useMutation({
    mutationFn: (imageId: string) => api.deleteImage(spaceId, itemId, imageId),
    onSuccess: rafraichir,
  })

  const [confirmeSuppression, setConfirmeSuppression] = useState(false)
  const [copie, setCopie] = useState<'oui' | 'non' | null>(null)
  const [messageLien, setMessageLien] = useState<string | null>(null)

  const supprimerDefinitivement = useMutation({
    mutationFn: () => api.deleteItem(spaceId, itemId),
    onSuccess: async () => {
      await rafraichir()
      void navigate(-1)
    },
  })

  /**
   * Le collage est écouté sur le document et non sur un élément de la page.
   *
   * Une `div` ne reçoit jamais le focus, donc jamais l'événement de collage : la zone « Images »
   * portait l'écoute et ne se déclenchait jamais. Dans la feuille de création, l'écoute est sur
   * la zone de saisie — focusable — d'où la différence de comportement que l'utilisateur a
   * constatée. Au niveau du document, coller marche depuis n'importe où sur la fiche.
   */
  const envoyer = envoyerImage.mutate

  useEffect(() => {
    const coller = (evenement: ClipboardEvent) => {
      const fichiers = Array.from(evenement.clipboardData?.files ?? []).filter((fichier) =>
        fichier.type.startsWith('image/'),
      )

      if (fichiers.length === 0) {
        return
      }

      evenement.preventDefault()

      for (const fichier of fichiers) {
        envoyer(fichier)
      }
    }

    document.addEventListener('paste', coller)
    return () => document.removeEventListener('paste', coller)
    // La dépendance est `mutate` et non l'objet de mutation : ce dernier est recréé à chaque
    // rendu, ce qui ferait se réabonner l'écoute sans arrêt. `mutate`, lui, est stable.
  }, [envoyer])

  const [ingredientsOuverts, setIngredientsOuverts] = useState(false)
  const [cadeauOuvert, setCadeauOuvert] = useState(false)
  const [destinataire, setDestinataire] = useState('')
  const [messageCadeau, setMessageCadeau] = useState<string | null>(null)
  const [choisis, setChoisis] = useState<string[]>([])
  const [messageCourses, setMessageCourses] = useState<string | null>(null)

  const ingredients = useQuery({
    queryKey: ['ingredients', spaceId, itemId],
    queryFn: () => api.ingredients(spaceId, itemId),
    enabled: ingredientsOuverts,
    // Une liste d'ingrédients ne bouge pas : inutile de relire la page à chaque affichage.
    staleTime: 60 * 60 * 1000,
  })

  const envoyerAuxCourses = useMutation({
    mutationFn: async () => {
      for (const article of choisis) {
        await api.addShoppingLine(spaceId, article, undefined, itemId)
      }
    },
    onSuccess: async () => {
      setMessageCourses(
        `${choisis.length} article${choisis.length > 1 ? 's' : ''} ajouté${choisis.length > 1 ? 's' : ''} aux courses.`,
      )
      setChoisis([])
      await queryClient.invalidateQueries({ queryKey: ['courses', spaceId] })
      await queryClient.invalidateQueries({ queryKey: ['aujourdhui'] })
    },
    onError: () => setMessageCourses("L'ajout a échoué."),
  })

  const categorieDeLaFiche = categories?.find((category) => category.id === item?.categoryId)

  // La fiche lien porte son adresse ; une note peut en contenir une dans son texte. Dans les deux
  // cas, c'est la même chose qu'on veut ouvrir ou copier.
  const lien = item?.url ?? premierLien(item?.description)

  const enFaireUnLien = useMutation({
    mutationFn: () => api.enFaireUnLien(spaceId, itemId),
    onSuccess: async () => {
      setMessageLien(null)
      await invaliderFiches(queryClient, spaceId)
    },
    onError: (cause: unknown) =>
      setMessageLien(cause instanceof ApiError ? cause.message : 'La transformation a échoué.'),
  })

  const [depenseOuverte, setDepenseOuverte] = useState(false)
  const [montant, setMontant] = useState('')
  const [messageDepense, setMessageDepense] = useState<string | null>(null)

  // Les dépenses déjà notées sur cette fiche. Rien à filtrer ici : une dépense rattachée à un
  // cadeau ne parvient pas jusqu'au destinataire, le serveur l'a déjà écartée.
  const depenses = useQuery<readonly Expense[]>({
    queryKey: ['depenses-fiche', spaceId, itemId],
    queryFn: () => api.itemExpenses(spaceId, itemId),
    enabled: Boolean(categorieDeLaFiche?.showExpense),
  })

  const noterLaDepense = useMutation({
    mutationFn: () =>
      api.createExpense(spaceId, {
        label: item?.title ?? 'Dépense',
        amountCents: Math.round(Number(montant.replace(',', '.')) * 100),
        itemId,
        ...(item?.categoryId ? { categoryId: item.categoryId } : {}),
      }),
    onSuccess: async () => {
      setMontant('')
      setDepenseOuverte(false)
      setMessageDepense('Dépense enregistrée.')
      await invaliderDepenses(queryClient, spaceId)
    },
    onError: (cause: unknown) =>
      setMessageDepense(cause instanceof ApiError ? cause.message : "L'ajout a échoué."),
  })

  const { data: espace } = useQuery<SpaceDetail>({
    queryKey: ['espace', spaceId],
    queryFn: () => api.space(spaceId),
  })

  const { data: me } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me })

  const enFaireUnCadeau = useMutation({
    mutationFn: () =>
      api.createGift(spaceId, {
        title: item?.title ?? 'Idée de cadeau',
        ...(destinataire ? { recipientUserId: destinataire } : {}),
        itemId: itemId,
      }),
    onSuccess: async () => {
      setMessageCadeau('Ajouté aux cadeaux.')
      await queryClient.invalidateQueries({ queryKey: ['cadeaux', spaceId] })
      await invaliderFiches(queryClient, spaceId)
    },
    onError: (cause: unknown) =>
      setMessageCadeau(cause instanceof ApiError ? cause.message : "L'ajout a échoué."),
  })

  const retirerEvenement = useMutation({
    mutationFn: (eventId: string) => api.deleteEvent(spaceId, eventId),
    onSuccess: async () => {
      await rafraichir()
      await queryClient.invalidateQueries({ queryKey: ['agenda', spaceId] })
      await queryClient.invalidateQueries({ queryKey: ['aujourdhui'] })
    },
  })

  const restaurer = useMutation({
    mutationFn: () => api.restoreItem(spaceId, itemId),
    onSuccess: rafraichir,
  })

  const archiver = useMutation({
    mutationFn: () => api.archiveItem(spaceId, itemId),
    onSuccess: async () => {
      await rafraichir()
      void navigate(-1)
    },
  })

  if (isPending || !item) {
    return (
      <main className="mx-auto min-h-dvh bg-surface" style={{ maxWidth: layout.column }}>
        <div className="w-full bg-skeleton-image" style={{ aspectRatio: '4 / 3' }} />
        <div style={{ paddingInline: layout.gutter, paddingBlock: 16 }}>
          <div className="rounded-field bg-sunken" style={{ height: 20 }} />
          <div className="mt-2 rounded-field bg-sunken" style={{ height: 13, width: '50%' }} />
        </div>
      </main>
    )
  }

  // Sous verrou et pas encore ouverte : l'écran ne montre que le cadenas et le champ du code.
  // Laisser la fiche derrière, vidée de son contenu, donnerait à chercher ce qui manque.
  if (item.isLocked && verrou.ouverte === null) {
    return <FicheProtegee item={item} verrou={verrou} />
  }

  const teinte = item.categoryHue

  // L'illustration de l'en-tête : la vignette du lien d'abord, à défaut la première photo de la
  // fiche. Sans ce repli, une fiche portant neuf photos ouvrait sur 140 px d'aplat vide — le
  // cas de « Nouuuusss », relevé le 2026-10-16.
  const vignetteDuLien = imageCassee ? null : item.imageUrl
  const illustration = vignetteDuLien ?? item.images[0]?.url ?? null

  // Quand c'est une photo de la fiche qui illustre l'en-tête, elle sort de la bande du bas :
  // sinon on la voit deux fois à trois centimètres d'intervalle, et la retirer d'un endroit
  // laisse l'autre. Même raison que pour les images de blocs (correction S83).
  const photos = vignetteDuLien === null ? item.images.slice(1) : item.images

  return (
    <main className="mx-auto min-h-dvh bg-surface" style={{ maxWidth: layout.column }}>
      <header className="relative">
        {illustration ? (
          <img
            src={illustration}
            alt=""
            onError={() => setImageCassee(true)}
            className="w-full object-cover"
            style={{ aspectRatio: '4 / 3' }}
          />
        ) : (
          // « Sans image, l'en-tête devient un aplat de catégorie de 140 px portant la marque. »
          <div
            className="flex items-center justify-center"
            style={{
              height: 140,
              background: teinte === null ? 'var(--color-sunken)' : `var(--color-cat-${teinte}-fill)`,
            }}
          >
            {item.categoryInitials && teinte !== null ? (
              <CategoryMark initials={item.categoryInitials} hue={teinte} size="large" />
            ) : null}
          </div>
        )}

        <div className="absolute inset-x-0 top-0 flex justify-between" style={{ padding: 12 }}>
          <Rond libelle="Retour" onClick={() => void navigate(-1)}>←</Rond>
          <div className="flex gap-2">
            <Rond
              libelle={item.isFavorite ? 'Retirer des favoris' : 'Mettre en favori'}
              onClick={() => favori.mutate(!item.isFavorite)}
            >
              {item.isFavorite ? '♥' : '♡'}
            </Rond>
            {/* Un seul bouton de retouche. Le crayon et le « ··· » menaient tous deux à
                « modifier » : deux portes pour une même intention, et il fallait se souvenir
                laquelle ouvrait quoi (correction S93). Le crayon ouvre désormais le tout. */}
            <Rond libelle="Modifier" onClick={() => setMenu((ouvert) => !ouvert)}>
              ✎
            </Rond>
          </div>
        </div>
      </header>

      <div style={{ paddingInline: layout.gutter, paddingBottom: 32 }}>
        {/* Une fiche archivée doit se signaler dès l'ouverture : sans cela on la croit vivante
            et on s'étonne de ne pas la retrouver dans les listes. */}
        {item.status === 'archived' ? (
          <p className="mt-4 rounded-card border border-border bg-sunken p-3 text-meta font-semibold">
            Fiche archivée. Ouvrez le crayon pour la restaurer.
          </p>
        ) : null}

        {item.categoryName && teinte !== null ? (
          <div className="mt-4 flex items-center gap-2">
            <CategoryMark initials={item.categoryInitials ?? '??'} hue={teinte} size="small" />
            <span className="text-meta font-semibold">{item.categoryName}</span>
          </div>
        ) : (
          <p className="mt-4 text-meta font-semibold text-ink-muted">À classer</p>
        )}

        {edition ? (
          <section className="mt-3">
            <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="titre">
              Titre
            </label>
            <input
              id="titre"
              value={titre}
              onChange={(event) => {
                setTitre(event.target.value)
                setErreurEdition(null)
              }}
              maxLength={300}
              className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
              style={{ height: 56, fontSize: 17 }}
            />

            <label
              className="mt-4 block text-label font-semibold uppercase text-ink-muted"
              htmlFor="texte"
            >
              Texte
            </label>
            <textarea
              id="texte"
              value={texte}
              onChange={(event) => {
                setTexte(event.target.value)
                setErreurEdition(null)
              }}
              maxLength={4000}
              rows={6}
              placeholder="Laissez vide pour ne rien afficher."
              className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card p-4 outline-none"
              style={{ fontSize: 16, lineHeight: 1.55 }}
            />

            <button
              type="button"
              disabled={titre.trim().length === 0 || enregistrer.isPending}
              onClick={() => enregistrer.mutate()}
              className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
              style={{ height: layout.primaryButtonHeight }}
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>

            <button
              type="button"
              onClick={() => setEdition(false)}
              className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
              style={{ height: layout.primaryButtonHeight }}
            >
              Annuler
            </button>

            {/* Le serveur refuse un titre vide : le bouton est désactivé avant d'y arriver. */}
            {titre.trim().length === 0 ? (
              <p className="mt-3 text-meta text-ink-muted">Le titre ne peut pas être vide.</p>
            ) : null}
            {erreurEdition ? <p className="mt-3 text-meta text-danger">{erreurEdition}</p> : null}
          </section>
        ) : (
          <>
            <h1 className="mt-3 flex items-start gap-2 font-serif text-record font-medium">
              {/* Ouverte, la fiche garde son cadenas : sans lui on oublie qu'on lit du protégé,
                  et on l'abandonne ouverte en passant le téléphone à quelqu'un. */}
              {item.isLocked ? (
                <span style={{ paddingTop: 6 }}>
                  <Cadenas taille={18} couleur="var(--color-ink-muted)" />
                </span>
              ) : null}
              {item.title}
            </h1>

            <p className="mt-2 text-meta text-ink-muted">
              {item.domain ? `${item.domain} · ` : ''}ajouté par {item.createdByName}
            </p>

            {/* Un titre qui ne fait que répéter le nom du site — « Facebook » pour une story
                derrière un mur de connexion — ne distingue cette fiche d'aucune autre. On ne
                peut pas le deviner à sa place, mais on peut le dire et ouvrir la retouche.
                Réservé aux fiches qui viennent d'un site : sur une note, écrite à la main, la
                phrase n'avait aucun sens — il n'y a pas de site (correction S88). */}
            {item.domain !== null && titreGenerique(item.title, item.domain) ? (
              <button
                type="button"
                onClick={() => {
                  setTitre(item.title)
                  setTexte(item.description ?? '')
                  setEdition(true)
                }}
                className="mt-3 w-full rounded-field border border-dashed border-border p-3 text-left text-meta text-ink-muted"
              >
                Ce titre vient du site et ne dit pas ce que c'est.{' '}
                <span className="font-semibold text-accent">Renommer la fiche</span>
              </button>
            ) : null}

            {item.description ? <TexteAvecLiens texte={item.description} /> : null}
          </>
        )}

        {/* Les blocs, sous le texte de la fiche. Une fiche sans bloc ne montre que les boutons
            d'ajout : la richesse est disponible partout, et n'encombre nulle part. */}
        <Blocs spaceId={spaceId} itemId={itemId} blocs={item.blocks} />

        {photos.length > 0 ? (
          <ul className="mt-4 flex flex-col gap-3">
            {photos.map((image) => (
              <li key={image.id}>
                <img
                  src={image.url}
                  alt=""
                  loading="lazy"
                  className="w-full rounded-card"
                  // Les dimensions réelles sont connues : les poser évite que la page saute
                  // quand l'image arrive.
                  width={image.width}
                  height={image.height}
                  style={{ height: 'auto' }}
                />
                <button
                  type="button"
                  onClick={() => retirerImage.mutate(image.id)}
                  className="mt-1 text-meta font-semibold text-ink-muted"
                >
                  Retirer cette image
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {/* Plus d'encart « Images » : le bouton « + Image » des blocs fait la même chose, et
            deux chemins pour une même action laissaient croire à deux fonctions différentes.
            Coller une image reste possible n'importe où sur la fiche — elle devient un bloc. */}
        {envoyerImage.isPending ? (
          <p className="mt-2 text-meta text-ink-muted">Envoi de l'image…</p>
        ) : null}
        {erreurImage ? <p className="mt-2 text-meta text-danger">{erreurImage}</p> : null}

        {/* « Une seule action primaire : ouvrir. »
            L'adresse vient de la fiche lien, ou à défaut du texte d'une note : une recette collée
            dans le texte est un lien pour qui l'a écrite, et n'en était pas un pour
            l'application (correction S70). */}
        {lien ? (
          <>
            <a
              href={lien}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-6 flex w-full items-center justify-center rounded-button bg-accent text-card-title font-semibold text-card"
              style={{ height: layout.primaryButtonHeight }}
            >
              Ouvrir le lien
            </a>

            {/* Une note qui contient une adresse peut devenir une vraie fiche lien : elle
                gagne le domaine, l'image et la carte que les autres recettes ont déjà. */}
            {item.type === 'note' ? (
              <>
                <button
                  type="button"
                  disabled={enFaireUnLien.isPending}
                  onClick={() => enFaireUnLien.mutate()}
                  className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold disabled:opacity-60"
                  style={{ height: layout.primaryButtonHeight }}
                >
                  {enFaireUnLien.isPending ? 'Lecture du site…' : 'En faire une fiche lien'}
                </button>

                {messageLien ? (
                  <p className="mt-2 text-meta text-danger">{messageLien}</p>
                ) : null}
              </>
            ) : null}

            <button
              type="button"
              onClick={async () => setCopie((await copier(lien)) ? 'oui' : 'non')}
              className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
              style={{ height: layout.primaryButtonHeight }}
            >
              {copie === 'oui'
                ? 'Lien copié'
                : copie === 'non'
                  ? 'Copie impossible — appui long sur le lien'
                  : 'Copier le lien'}
            </button>
          </>
        ) : null}

        {/* Rendu de l'action « Aux courses » retirée le 2026-08-19, mais cette fois elle sait
            lire les ingrédients au lieu de demander de les retaper (point ouvert P8). On propose,
            l'utilisateur choisit : un site liste souvent ce qu'on a déjà dans son placard.

            Affiché seulement si la catégorie l'annonce : le bouton apparaissait partout, y
            compris sur une fiche de cadeau, où il n'a aucun sens (correction S59). */}
        {categorieDeLaFiche?.showIngredients ? (
          <>
            <button
              type="button"
              onClick={() => {
                setIngredientsOuverts((ouvert) => !ouvert)
                setMessageCourses(null)
              }}
              className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
              style={{ height: layout.primaryButtonHeight }}
            >
              Ingrédients aux courses
            </button>

            {ingredientsOuverts ? (
          <section className="mt-3 rounded-card border border-border bg-card p-4">
            {ingredients.isPending ? (
              <p className="text-meta text-ink-muted">Lecture de la recette…</p>
            ) : ingredients.data && ingredients.data.ingredients.length > 0 ? (
              <>
                <p className="text-meta text-ink-muted">
                  {ingredients.data.source === 'page'
                    ? 'Lus sur la page de la recette. Cochez ce qui vous manque.'
                    : 'Lus dans le texte de la fiche, une ligne par article.'}
                </p>

                <ul className="mt-3 flex flex-col gap-2">
                  {ingredients.data.ingredients.map((article) => (
                    <li key={article}>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={choisis.includes(article)}
                          onChange={(evenement) =>
                            setChoisis((deja) =>
                              evenement.target.checked
                                ? [...deja, article]
                                : deja.filter((autre) => autre !== article),
                            )
                          }
                          style={{ width: 22, height: 22, flexShrink: 0 }}
                        />
                        <span className="text-body">{article}</span>
                      </label>
                    </li>
                  ))}
                </ul>

                <div className="mt-3 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setChoisis([...ingredients.data.ingredients])}
                    className="text-meta font-semibold text-accent"
                  >
                    Tout cocher
                  </button>
                  {choisis.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setChoisis([])}
                      className="text-meta font-semibold text-ink-muted"
                    >
                      Tout décocher
                    </button>
                  ) : null}
                </div>

                <button
                  type="button"
                  disabled={choisis.length === 0 || envoyerAuxCourses.isPending}
                  onClick={() => envoyerAuxCourses.mutate()}
                  className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
                  style={{ height: layout.primaryButtonHeight }}
                >
                  {envoyerAuxCourses.isPending
                    ? 'Ajout…'
                    : choisis.length === 0
                      ? 'Choisissez des articles'
                      : `Ajouter ${choisis.length} article${choisis.length > 1 ? 's' : ''}`}
                </button>
              </>
            ) : (
              <p className="text-meta text-ink-muted">
                Aucun ingrédient trouvé. Le site ne publie pas sa liste sous une forme lisible, et
                la fiche n'a pas de texte à découper.
              </p>
            )}

            {messageCourses ? (
              <p className="mt-3 text-meta text-accent">{messageCourses}</p>
            ) : null}
          </section>
        ) : null}
        </>
        ) : null}

        {/* En faire une idée de cadeau, quand la catégorie s'y prête. Le cadeau est masqué à son
            destinataire dès qu'on le désigne — et la fiche elle-même disparaît pour lui. */}
        {categorieDeLaFiche?.showGift ? (
          <>
            <button
              type="button"
              onClick={() => {
                setCadeauOuvert((ouvert) => !ouvert)
                setMessageCadeau(null)
              }}
              className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
              style={{ height: layout.primaryButtonHeight }}
            >
              En faire un cadeau
            </button>

            {cadeauOuvert ? (
              <section className="mt-3 rounded-card border border-border bg-card p-4">
                <label
                  className="block text-label font-semibold uppercase text-ink-muted"
                  htmlFor="destinataire-fiche"
                >
                  Pour qui
                </label>
                <select
                  id="destinataire-fiche"
                  value={destinataire}
                  onChange={(evenement) => setDestinataire(evenement.target.value)}
                  className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
                  style={{ height: 56, fontSize: 17 }}
                >
                  <option value="">Quelqu'un hors de l'espace</option>
                  {espace?.members.map((membre) => (
                    <option key={membre.userId} value={membre.userId}>
                      {membre.displayName}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-meta text-ink-muted">
                  Choisir un membre lui cache le cadeau <b>et cette fiche</b> : il ne la verra
                  plus nulle part.
                </p>

                <button
                  type="button"
                  disabled={enFaireUnCadeau.isPending}
                  onClick={() => enFaireUnCadeau.mutate()}
                  className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
                  style={{ height: layout.primaryButtonHeight }}
                >
                  {enFaireUnCadeau.isPending ? 'Ajout…' : 'Ajouter aux cadeaux'}
                </button>

                {messageCadeau ? (
                  <p className="mt-3 text-meta text-accent">{messageCadeau}</p>
                ) : null}
              </section>
            ) : null}
          </>
        ) : null}

        {/* Ce que la fiche a coûté, quand la catégorie s'y prête (décision L37 étendue).
            Sur un cadeau, la dépense n'est pas partagée avec son destinataire : elle ne lui est
            pas montrée, et son solde ne bouge pas — sans quoi le montant se devinerait. */}
        {categorieDeLaFiche?.showExpense ? (
          <>
            {depenses.data && depenses.data.length > 0 ? (
              <section className="mt-4 rounded-card border border-border bg-card p-4">
                <p className="text-label font-semibold uppercase text-ink-muted">Ce que ça a coûté</p>
                <ul className="mt-3 flex flex-col gap-2">
                  {depenses.data.map((depense) => (
                    <li key={depense.id} className="flex items-baseline justify-between gap-3">
                      <span className="grow truncate text-meta">
                        {depense.paidByName} ·{' '}
                        {new Date(depense.occurredOn).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="shrink-0 text-card-title font-semibold">
                        {euros(depense.amountCents)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <button
              type="button"
              onClick={() => {
                setDepenseOuverte((ouvert) => !ouvert)
                setMessageDepense(null)
              }}
              className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
              style={{ height: layout.primaryButtonHeight }}
            >
              Noter une dépense
            </button>

            {depenseOuverte ? (
              <section className="mt-3 rounded-card border border-border bg-card p-4">
                <label
                  className="block text-label font-semibold uppercase text-ink-muted"
                  htmlFor="montant-fiche"
                >
                  Combien
                </label>
                <input
                  id="montant-fiche"
                  autoFocus
                  inputMode="decimal"
                  value={montant}
                  onChange={(evenement) => {
                    setMontant(evenement.target.value)
                    setMessageDepense(null)
                  }}
                  placeholder="42,50"
                  className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
                  style={{ height: 56, fontSize: 17 }}
                />

                <p className="mt-2 text-meta text-ink-muted">
                  {item.hiddenFromSomeone
                    ? "C'est un cadeau : la dépense reste pour vous, son destinataire n'en verra ni le montant ni la trace dans le solde."
                    : 'La dépense rejoint les dépenses partagées, partagée en parts égales.'}
                </p>

                {messageDepense ? (
                  <p className="mt-3 text-meta text-accent">{messageDepense}</p>
                ) : null}

                <button
                  type="button"
                  disabled={
                    Number(montant.replace(',', '.')) <= 0 || noterLaDepense.isPending
                  }
                  onClick={() => noterLaDepense.mutate()}
                  className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
                  style={{ height: layout.primaryButtonHeight }}
                >
                  {noterLaDepense.isPending ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </section>
            ) : null}
          </>
        ) : null}

        {/* Les rendez-vous nés de cette fiche, et le moyen de les retirer d'ici. Sans cette
            liste, enlever une date envoyée à l'agenda obligeait à la retrouver dans l'agenda,
            sans indice de sa provenance (correction S64). */}
        {item.events.length > 0 ? (
          <section className="mt-4 rounded-card border border-border bg-card p-4">
            <p className="text-label font-semibold uppercase text-ink-muted">À l'agenda</p>
            <ul className="mt-3 flex flex-col gap-2">
              {item.events.map((evenement) => (
                <li key={evenement.id} className="flex items-center justify-between gap-3">
                  <span className="grow truncate text-meta">
                    {new Date(evenement.startsAt).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                    {evenement.isAllDay
                      ? ''
                      : ` à ${new Date(evenement.startsAt).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => retirerEvenement.mutate(evenement.id)}
                    className="shrink-0 text-meta font-semibold text-ink-muted"
                  >
                    Retirer
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <button
          type="button"
          onClick={() => setAgenda((ouvert) => !ouvert)}
          className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
          style={{ height: layout.primaryButtonHeight }}
        >
          À l'agenda
        </button>

        {agenda ? (
          <section className="mt-3 rounded-card border border-border bg-card p-4">
            <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="quand">
              Quel jour ?
            </label>
            <input
              id="quand"
              type="date"
              value={quand}
              onChange={(event) => {
                setQuand(event.target.value)
                setAgendaMessage(null)
              }}
              className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
              style={{ height: 56, fontSize: 17 }}
            />

            <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="jusqua">
              Jusqu'au (facultatif)
            </label>
            <input
              id="jusqua"
              type="date"
              value={jusqua}
              min={quand}
              onChange={(event) => {
                setJusqua(event.target.value)
                setAgendaMessage(null)
              }}
              className="mt-2 w-full rounded-field border border-border bg-card px-4 outline-none"
              style={{ height: 56, fontSize: 17 }}
            />

            <p className="mt-2 text-meta text-ink-muted">
              L'événement gardera un lien vers cette fiche.
            </p>

            {/* Un bouton explicite : la version précédente enregistrait au changement du champ,
                donc sans confirmation ni moment clair. */}
            <button
              type="button"
              disabled={quand.length === 0 || envoyerAgenda.isPending}
              onClick={() => envoyerAgenda.mutate()}
              className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
              style={{ height: layout.primaryButtonHeight }}
            >
              {envoyerAgenda.isPending ? 'Ajout…' : 'Ajouter à l\'agenda'}
            </button>

            {agendaMessage ? (
              <p className="mt-3 text-meta text-accent">{agendaMessage}</p>
            ) : null}
          </section>
        ) : null}

        {menu ? (
          <section className="mt-6 rounded-card border border-border bg-card p-4">
            {/* En tête, parce que c'est ce qu'on vient faire neuf fois sur dix. */}
            <button
              type="button"
              onClick={() => {
                setMenu(false)
                ouvrirEdition()
              }}
              className="mb-4 w-full rounded-button bg-accent text-card-title font-semibold text-card"
              style={{ height: layout.primaryButtonHeight }}
            >
              Modifier le titre et le texte
            </button>

            {/* La protection avant le classement : c'est une décision sur la fiche entière, là
                où la catégorie n'est qu'un rangement. */}
            <div className="mb-4">
              <ReglagesDuVerrou
                spaceId={spaceId}
                item={item}
                membres={espace?.members ?? []}
                moi={me?.id ?? ''}
                aUnCode={me?.hasPin ?? false}
                estAdministration={espace?.viewerIsAdminGuest ?? false}
                verrou={verrou}
                onChange={rafraichir}
              />
            </div>

            <p className="text-label font-semibold uppercase text-ink-muted">Changer de catégorie</p>
            <ul className="mt-3 grid grid-cols-2" style={{ gap: layout.gridGap }}>
              {categories?.map((category) => (
                <li key={category.id}>
                  <button
                    type="button"
                    onClick={() => classer.mutate(category.id)}
                    className="flex w-full items-center gap-2 rounded-card p-3 text-left"
                    style={{ background: `var(--color-cat-${category.hue}-fill)`, minHeight: layout.minimumTouchTarget }}
                  >
                    <CategoryMark initials={category.initials} hue={category.hue} size="small" />
                    <span className="text-meta font-semibold">{category.name}</span>
                  </button>
                </li>
              ))}
            </ul>

            {item.status === 'archived' ? (
              <>
                <button
                  type="button"
                  onClick={() => restaurer.mutate()}
                  className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card"
                  style={{ height: layout.primaryButtonHeight }}
                >
                  Restaurer
                </button>
                <p className="mt-2 text-meta text-ink-muted">
                  La fiche revient dans vos listes, avec sa catégorie et son cœur.
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => archiver.mutate()}
                  className="mt-4 w-full rounded-button border border-border bg-card text-card-title font-semibold"
                  style={{ height: layout.primaryButtonHeight }}
                >
                  Archiver
                </button>
                <p className="mt-2 text-meta text-ink-muted">
                  La fiche disparaît de vos listes et se retrouve dans l'onglet « Archivées ».
                </p>
              </>
            )}

            {/* La suppression définitive est proposée dans les deux cas, et non plus seulement
                depuis les archives : passer par l'archivage pour effacer quelque chose dont on
                ne veut plus était un détour sans raison. Le libellé « Archiver » redevient
                possible sans ambiguïté puisque les deux gestes sont côte à côte, chacun avec sa
                conséquence écrite. */}
            {confirmeSuppression ? (
              <>
                <button
                  type="button"
                  disabled={supprimerDefinitivement.isPending}
                  onClick={() => supprimerDefinitivement.mutate()}
                  className="mt-4 w-full rounded-button text-card-title font-semibold text-card disabled:opacity-60"
                  style={{
                    height: layout.primaryButtonHeight,
                    background: 'var(--color-danger)',
                  }}
                >
                  {supprimerDefinitivement.isPending
                    ? 'Suppression…'
                    : 'Confirmer la suppression définitive'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmeSuppression(false)}
                  className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
                  style={{ height: layout.primaryButtonHeight }}
                >
                  Annuler
                </button>
                <p className="mt-2 text-meta" style={{ color: 'var(--color-danger)' }}>
                  La fiche et ses images seront effacées pour de bon. Rien ne permettra de les
                  retrouver.
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setConfirmeSuppression(true)}
                  className="mt-3 w-full rounded-button border bg-card text-card-title font-semibold"
                  style={{
                    height: layout.primaryButtonHeight,
                    borderColor: 'var(--color-danger)',
                    color: 'var(--color-danger)',
                  }}
                >
                  Supprimer définitivement
                </button>
                <p className="mt-2 text-meta text-ink-muted">
                  Sans retour possible. Une confirmation sera demandée.
                </p>
              </>
            )}
          </section>
        ) : null}
      </div>
    </main>
  )
}

function Rond({
  children,
  libelle,
  onClick,
}: {
  children: ReactNode
  libelle: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={libelle}
      onClick={onClick}
      className="flex items-center justify-center rounded-full bg-card text-card-title font-semibold shadow-card"
      style={{ width: layout.minimumTouchTarget, height: layout.minimumTouchTarget }}
    >
      {children}
    </button>
  )
}
